import { AddOutlined, CloseOutlined } from '@mui/icons-material';
import { Box, Button, TextField, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Media } from '@synergy/models';
import { PaloModal } from '@synergy/components-palo';
import { PoolDropzone, PoolFileList } from '@synergy/components-next';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

export interface PaloAttachmentGroup {
    key: string;
    /** Named only when a document keeps more than one set of files. */
    label?: ReactNode;
    files: Media[];
    downloadBaseUrl: string;
    /** `titles` holds one entry per file, empty where none was given. */
    onDrop?: (files: File[], titles?: string[]) => void;
    onRemove?: (media: Media) => void;
    /** Names a stored file. Offered on every file of the group that is not read-only. */
    onRename?: (media: Media, title: string) => void;
    onCancel?: (media: Media) => void;
    readOnly?: boolean;
    /**
     * Files that belong to a position of the document. They are listed here so
     * the rail shows everything the document carries, but they are managed at
     * the position, where their assignment is visible (key-user brief A3.3).
     */
    positionFiles?: Media[];
    /** Why dropping is not possible right now, e.g. "save the document first". */
    hint?: ReactNode;
    /**
     * Where the files go, under the heading — e.g. "goes with every price
     * info". Shown with or without files: it is what one needs to know before
     * adding one (#2256).
     */
    note?: ReactNode;
    /** The heading in ink rather than grey: the group the open tab is about. */
    emphasized?: boolean;
    /** No heading while the group holds nothing — for groups only filled by the system. */
    hideWhenEmpty?: boolean;
}

export interface PaloAttachmentsProps {
    groups: PaloAttachmentGroup[];
}

/**
 * The files of a document, in the rail beside the mask.
 *
 * Each group is one line: what it is called, how many files it holds, and a
 * plus. The drop zone used to stand open underneath — 144 pixels of dashed
 * border per group, permanently, whether or not anyone wanted to add a file.
 * In a rail that is meant to carry the attachments and later the tags and the
 * sharing, that is most of the column spent on an invitation.
 *
 * It follows ERPNext, which the customer holds up as the example: the sidebar
 * shows a line with a plus, and the drop zone lives in the dialog behind it.
 * Dropping onto the group still works — the zone only stopped being drawn.
 *
 * The groups survive: a document keeps what goes out to the other side apart
 * from what stays inside, and merging them would send the wrong file to the
 * wrong place.
 */
export const PaloAttachments = ({ groups }: PaloAttachmentsProps): ReactElement => {
    const intl = useIntl();
    const [uploadTo, setUploadTo] = useState<string | undefined>(undefined);
    const [over, setOver] = useState<string | undefined>(undefined);
    /**
     * Picked but not sent yet. The dialog collects first and uploads on the
     * button, so several files can be gathered, checked and taken off the list
     * again before anything leaves the machine.
     */
    const [picked, setPicked] = useState<File[]>([]);
    /** One per picked file. Optional: a file without one shows its name. */
    const [titles, setTitles] = useState<string[]>([]);
    const [renaming, setRenaming] = useState<{ group: PaloAttachmentGroup; media: Media } | undefined>(undefined);
    const [newTitle, setNewTitle] = useState('');

    const open = groups.find(group => group.key === uploadTo);

    /** Headings alone read as a broken panel, so say that nothing is there. */
    const empty = groups.every(group => 0 === group.files.length + (group.positionFiles?.length ?? 0));

    const closeDialog = (): void => {
        setUploadTo(undefined);
        setPicked([]);
        setTitles([]);
    };

    const send = (): void => {
        open?.onDrop?.(picked, titles);
        closeDialog();
    };

    const startRename = (group: PaloAttachmentGroup) => (media: Media) => {
        setRenaming({ group, media });
        setNewTitle(media.title ?? '');
    };

    const saveRename = (): void => {
        renaming?.group.onRename?.(renaming.media, newTitle);
        setRenaming(undefined);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {groups.map(group => {
                const droppable = !group.readOnly && Boolean(group.onDrop);

                if (group.hideWhenEmpty && 0 === group.files.length + (group.positionFiles?.length ?? 0)) {
                    return null;
                }

                return (
                    <Box
                        key={group.key}
                        onDragOver={event => {
                            if (droppable) {
                                event.preventDefault();
                                setOver(group.key);
                            }
                        }}
                        onDragLeave={() => setOver(undefined)}
                        onDrop={event => {
                            setOver(undefined);

                            if (!droppable) {
                                return;
                            }

                            event.preventDefault();
                            const files = Array.from(event.dataTransfer?.files ?? []);

                            if (files.length > 0) {
                                group.onDrop?.(files);
                            }
                        }}
                        sx={{
                            borderRadius: tokens.radius.small + 'px',
                            // Shown only while something hovers: the target is
                            // there, it just does not ask for the space.
                            outline: over === group.key ? `1px dashed ${c.accent}` : 'none',
                            backgroundColor: over === group.key ? c.accentSoft : 'transparent',
                            paddingBottom: '6px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                minHeight: 30,
                                fontSize: '10.5px',
                                fontWeight: 640,
                                letterSpacing: '.08em',
                                textTransform: 'uppercase',
                                color: group.emphasized ? c.ink : c.ink3,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>{group.label}</Box>

                            {/* Unnamed, the group is the whole rail section, whose
                                heading already carries the count. */}
                            {Boolean(group.label) && group.files.length + (group.positionFiles?.length ?? 0) > 0 && (
                                <Box
                                    component="span"
                                    sx={{
                                        fontWeight: 500,
                                        letterSpacing: 'normal',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {group.files.length + (group.positionFiles?.length ?? 0)}
                                </Box>
                            )}

                            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                {droppable ? (
                                    <Tooltip title={<FormattedMessage id="app.attachments.add" />}>
                                        <Box
                                            role="button"
                                            aria-label="Datei hinzufügen"
                                            onClick={() => setUploadTo(group.key)}
                                            sx={{
                                                display: 'flex',
                                                cursor: 'pointer',
                                                color: c.ink3,
                                                '&:hover': { color: c.accentHi },
                                                '& svg': { fontSize: 17 },
                                            }}
                                        >
                                            <AddOutlined />
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    // Not droppable and not read-only means the
                                    // document has to be saved first — say why
                                    // rather than showing a plus that does nothing.
                                    !group.readOnly &&
                                    group.hint && (
                                        <Tooltip title={group.hint}>
                                            <Box sx={{ fontSize: '10px', fontWeight: 400, textTransform: 'none' }}>
                                                <FormattedMessage id="app.upload.notYet" />
                                            </Box>
                                        </Tooltip>
                                    )
                                )}
                            </Box>
                        </Box>

                        {group.note && (
                            <Box
                                sx={{
                                    mb: '6px',
                                    padding: '3px 7px',
                                    fontSize: '11px',
                                    lineHeight: 1.4,
                                    color: c.warnInk,
                                    backgroundColor: c.warnSoft,
                                    border: `1px solid ${c.warnLine}`,
                                    borderRadius: tokens.radius.small + 'px',
                                }}
                            >
                                {group.note}
                            </Box>
                        )}

                        {group.files.length > 0 && (
                            <PoolFileList
                                acceptedFiles={group.files}
                                downloadBaseUrl={group.downloadBaseUrl}
                                onCancelClicked={group.onCancel}
                                onDeleteClicked={group.onRemove}
                                onEditClicked={group.onRename ? startRename(group) : undefined}
                                readOnly={group.readOnly}
                                hideTitle={true}
                            />
                        )}

                        {(group.positionFiles?.length ?? 0) > 0 && (
                            <PoolFileList
                                acceptedFiles={group.positionFiles}
                                downloadBaseUrl={group.downloadBaseUrl}
                                readOnly={true}
                                hideTitle={true}
                            />
                        )}
                    </Box>
                );
            })}

            {empty && (
                <Box sx={{ fontSize: '12px', color: c.ink3, paddingTop: '2px' }}>
                    <FormattedMessage id="app.attachments.empty" />
                </Box>
            )}

            <PaloModal
                isVisible={Boolean(open)}
                title={<FormattedMessage id="app.attachments.add" />}
                titleComponent={open?.label}
                handleClose={closeDialog}
                width="sm"
                actionChildren={
                    <>
                        <Button variant="outlined" color="inherit" size="small" onClick={closeDialog}>
                            <FormattedMessage id="app.common.cancel" />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={0 === picked.length}
                            onClick={send}
                        >
                            <FormattedMessage id="app.attachments.upload" values={{ count: picked.length }} />
                        </Button>
                    </>
                }
            >
                {open && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <PoolDropzone onDrop={files => setPicked(current => [...current, ...files])} />

                        {picked.map((file, index) => (
                            <Box
                                key={`${file.name}-${index}`}
                                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        fontSize: '13px',
                                        color: c.ink,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{file.name}</Box>
                                    <Box sx={{ color: c.ink3, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                        {Math.max(1, Math.round(file.size / 1024))} KB
                                    </Box>
                                    <Box
                                        role="button"
                                        aria-label="Datei aus der Auswahl nehmen"
                                        onClick={() => {
                                            setPicked(current => current.filter((_, at) => at !== index));
                                            setTitles(current => current.filter((_, at) => at !== index));
                                        }}
                                        sx={{
                                            display: 'flex',
                                            cursor: 'pointer',
                                            color: c.ink3,
                                            flexShrink: 0,
                                            '&:hover': { color: c.crit },
                                            '& svg': { fontSize: 16 },
                                        }}
                                    >
                                        <CloseOutlined />
                                    </Box>
                                </Box>
                                <TextField
                                    size="small"
                                    fullWidth={true}
                                    placeholder={intl.formatMessage({ id: 'app.attachments.titleOptional' })}
                                    value={titles[index] ?? ''}
                                    inputProps={{ maxLength: 255, 'aria-label': `Titel für ${file.name}` }}
                                    onChange={event => {
                                        const value = event.target.value;
                                        setTitles(current => {
                                            const next = [...current];
                                            next[index] = value;
                                            return next;
                                        });
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </PaloModal>

            <PaloModal
                isVisible={Boolean(renaming)}
                title={<FormattedMessage id="app.attachments.rename" />}
                handleClose={() => setRenaming(undefined)}
                width="sm"
                actionChildren={
                    <>
                        <Button variant="outlined" color="inherit" size="small" onClick={() => setRenaming(undefined)}>
                            <FormattedMessage id="app.common.cancel" />
                        </Button>
                        <Button variant="contained" color="primary" size="small" onClick={saveRename}>
                            <FormattedMessage id="app.common.save" />
                        </Button>
                    </>
                }
            >
                {renaming && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Box sx={{ fontSize: '12px', color: c.ink3, wordBreak: 'break-all' }}>
                            {renaming.media.originalName}
                        </Box>
                        <TextField
                            size="small"
                            fullWidth={true}
                            autoFocus={true}
                            placeholder={intl.formatMessage({ id: 'app.label.title' })}
                            value={newTitle}
                            inputProps={{ maxLength: 255, 'aria-label': 'Titel' }}
                            helperText={<FormattedMessage id="app.attachments.renameHint" />}
                            onChange={event => setNewTitle(event.target.value)}
                            // On release, not on press: closing hands the focus
                            // back to the pencil, and a press still under way
                            // would open the dialog again.
                            onKeyUp={event => {
                                if ('Enter' === event.key) {
                                    saveRename();
                                }
                            }}
                        />
                    </Box>
                )}
            </PaloModal>
        </Box>
    );
};
